//#region imports
import React, { useEffect, useState } from "react";

// custom components
import { CustomSpinner } from "_atoms";
import JobCard from "./job-card";
import TaskAssignmentTimeWarning from "./task-assignment-time-warning";
import PickupDropOffDropDowns from "./pickup-dropoff-dropdowns";
import PaxAssignmentText from "./pax-assignment-text";
import TabSwitcherSmall from "_components/atoms/tab-switcher-small";
import CreateAdhocTask from "../timeline-components/create-adhoc-task";

// bootstrap
import Button from "react-bootstrap/esm/Button";
import { Col, Form, Row } from "react-bootstrap";

// tremor
import { Subtitle } from "@tremor/react";

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectLatestJob, createTask, selectCreateTaskStatus, clearCreateTaskStatus, selectCreateTaskError, setTimelineData, updateTask, selectTaskList, selectUpdateTaskStatus, selectLastTaskQueued, resetUpdateTaskFlags, paxCheckIn, selectPaxCheckInStatus, resetPaxCheckInFlags, selectPassengers, setBookingLocation } from "_redux/slices/timeline/timeline-data";
import { selectFirebaseInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";
import { sendEmployeeNotification, selectEmployeeList, updateEmployee, selectClickedEmployeeId, } from "_redux/slices/timeline/employees";
import { selectSelectedTasksList, selectSelectedTask, setSelectedTaskList, setSelectedTask, selectSelectedMultiplePAXTask, setSelectedMultiplePAXTask, selectSelectedMultiplePAXTaskError, setSelectedMultiplePAXTaskError, setAdhocTaskEmployee } from "_redux/slices/timeline/selected-task";
import { selectSelectedTimeline, selectDropOffLocations, selectPickUpLocations } from "_redux/slices/timeline/timeline-areas";
import { selectDefaultCheckInLocation, selectThirtyMinLayout } from "_redux/slices/timeline/timeline-control";
import { createFlightConversation, selectFlightListIndexed } from "_redux/slices/timeline/flight-information-list";
import { setToast, setShowToast } from "_redux/slices/timeline/timeline-toast";
import { selectUsedTheme } from "_redux/slices/timeline/theme";

import { getFlightById, resetGetFlightFromDBById, selectFlightById, selectFlightFromDBById, selectFlightsArray, selectGetFlightByIdStatus, getThirdStateAirports, selectThirdStateAirports } from "_redux/slices/timeline/timeline-flight-list";
import { removeClientDuplicate } from "_redux/slices/timeline/timeline-bookings";

// styling
import { Colors, Spacing } from "_styles";
import * as JobConfirmationStyles from "./job-confirmation-styles"
import { IoWarning } from "react-icons/io5";


// functions
import { getSeatNumber, processName, isNormalTask, getEmployeeStatusForAppVersion } from "_utils/functions";
import { getFlightById as getFlightByIdService } from "_services/flight-service";
import BookingNotesSuggestions, { normalizeBookingNotes } from "_components/molecules/assistance-bookings/booking-notes-suggestions";
import ConnectionBanner from "../utils/connection-banner";

import { SaudiDomesticAirports } from "../timeline-constants";

//#endregion
/** Destinations requiring 60-min boarding for DUB departures (US/CAN/Caribbean) */
const SIXTY_MIN_BOARDING_DESTINATIONS = [
  'JFK', 'EWR', 'BOS', 'ORD', 'LAX', 'MIA', 'MCO', 'SFO', 'SEA',
  'IAD', 'PHL', 'CLE', 'IND', 'BNA', 'RDU', 'YYZ', 'DEN', 'LAS',
  'BDL', 'CUN', 'MSP', 'BGI'
];

/** Airlines requiring 60-min boarding for DUB departures */
const SIXTY_MIN_BOARDING_AIRLINES = ['AA', 'AC', 'B6', 'DL', 'TS', 'UA', 'WS', 'QR', 'EY', 'EK', 'HU'];

const getBoardingTime = (departureFlight, std, seats, iata = null) => {
  try {
    if (!std) {
      return null;
    }

    const depTime = new Date(std);
    if (isNaN(depTime.getTime())) {
      return null;
    }


    // check if not dublin - use 45 min boarding for non-DUB airports
    if (iata && iata !== 'DUB') {
      const boardingDate = new Date(std);
      boardingDate.setMinutes(boardingDate.getMinutes() - 45);
      return boardingDate.toISOString();
    }

    // Calculate boarding offset based on seat capacity
    // small aircraft (equal to or less than 230 seats): 35 minutes before
    // large aircraft (equal to or more than 231 seats): 50 minutes before
    let boardingOffsetMinutes = 35; // default

    // DUB departures to US/CAN/Caribbean destinations or on specified airlines: 60 min boarding
    const destinationIata = departureFlight?.arrivalAirport;
    const carrierIata = departureFlight?.carrierIATA || departureFlight?.flightNumber?.slice(0, 2);
    const isDublinDeparture = departureFlight?.departureAirport === 'DUB';
    const isSixtyMinDestination = destinationIata && SIXTY_MIN_BOARDING_DESTINATIONS.includes(destinationIata);
    const isSixtyMinAirline = carrierIata && SIXTY_MIN_BOARDING_AIRLINES.includes(carrierIata);
    if (isDublinDeparture && (isSixtyMinDestination || isSixtyMinAirline)) {
      const boardingDate = new Date(std);
      boardingDate.setMinutes(boardingDate.getMinutes() - 60);
      return boardingDate.toISOString();
    }

    if (seats) {

      // parse seats to integer
      const seatCount = parseInt(seats, 10);

      // only adjust if seatCount is a valid number
      if (!isNaN(seatCount)) {

        // adjust boarding offset based on seat count
        if (seatCount <= 230) {
          boardingOffsetMinutes = 35;
        }
        else {
          boardingOffsetMinutes = 50;
        }

      }

    }

    // check if flight has a manual boarding time override
    if (departureFlight?.manualBoardingTime) {
      return departureFlight.manualBoardingTime;
    }

    // Calculate boarding time by subtracting offset from STD
    depTime.setMinutes(depTime.getMinutes() - boardingOffsetMinutes);

    return depTime.toISOString();
  } catch (err) {
    console.warn("Error calculating boarding time:", err);
    return null;
  }
};
const getIataTimezone = (iata) => {
  if (SaudiDomesticAirports.includes(iata)) return 'Asia/Riyadh';
  if (iata === 'PUJ') return 'America/Santo_Domingo';
  return 'Europe/Dublin';
};

const normalizePaxNoteEntry = (note) => {
  if (!note || typeof note !== "object") return null;

  const text = typeof note?.text === "string" ? note.text.trim() : "";
  if (!text) return null;

  return {
    time: String(note?.time ?? note?.timestamp ?? ""),
    userName: String(note?.userName ?? note?.author ?? ""),
    userId: String(note?.userId ?? note?.uid ?? ""),
    text: String(text),
  };
};

const normalizeAndMergePaxNotes = (notes) => {
  if (!Array.isArray(notes)) return [];

  const noteKeySet = new Set();
  const mergedNotes = [];

  notes.forEach((note) => {
    const normalizedNote = normalizePaxNoteEntry(note);
    if (!normalizedNote) return;

    const noteKey = `${normalizedNote.userId}::${normalizedNote.text.toLowerCase()}`;
    if (noteKeySet.has(noteKey)) return;

    noteKeySet.add(noteKey);
    mergedNotes.push(normalizedNote);
  });

  return mergedNotes;
};

// function to check if origin iata is in third state airports list
const isFromThirdState = (iata, thirdStateList) => {

  if (!iata || iata.trim() === "" || !Array.isArray(thirdStateList)) return false;

  const normalizedIata = iata.trim().toUpperCase();

  const res = thirdStateList.includes(normalizedIata);

  return res;
};

const buildFlightDocumentFromDB = (flightFromDBById, iata, thirdStateList) => {
  if (!flightFromDBById) return null;

  const originalFlightTimeConverted = new Date(flightFromDBById?.originalFlightTime)?.toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });

  // get scheduled date
  const scheduledDate = flightFromDBById?.originalFlightTime
    ? typeof flightFromDBById?.originalFlightTime === 'string'
      ? flightFromDBById?.originalFlightTime?.split('T')?.[0]
      : new Date(flightFromDBById?.originalFlightTime)?.toISOString()?.split('T')?.[0]
    : '';

  // get flight date
  const flightDate = flightFromDBById?.operationInfo?.time
    ? typeof flightFromDBById?.operationInfo.time === 'string'
      ? flightFromDBById?.operationInfo?.time?.split('T')?.[0]
      : new Date(flightFromDBById?.operationInfo.time)?.toISOString()?.split('T')?.[0]
    : '';

  const isOriginInThirdState = isFromThirdState(flightFromDBById?.departureAirport, thirdStateList);

  return {
    ...flightFromDBById,
    flightId: flightFromDBById?._id ?? null,
    bookingKey: `${flightFromDBById?.flightNumber}_${flightFromDBById?.departureAirport}_${flightFromDBById?.arrivalAirport}_${scheduledDate}`,
    flightDate: flightFromDBById?.flightDate || flightFromDBById?.date || originalFlightTimeConverted || flightDate,
    status: flightFromDBById?.flightStatus == 'X' || flightFromDBById?.flightStatusCodeCDM == 'CNL' ? 'cancelled' : flightFromDBById?.operationInfo?.status,
    statusType: flightFromDBById?.operationInfo?.statusType,
    airline: flightFromDBById?.flightNumber?.slice(0, 2) ?? "",
    origin: flightFromDBById?.departureAirport,
    destination: flightFromDBById?.arrivalAirport,
    scheduled: flightFromDBById?.originalFlightTime,
    operationTime: flightFromDBById?.operationInfo?.time,
    cancelled: flightFromDBById?.flightStatus == 'X' || flightFromDBById?.flightStatusCodeCDM == 'CNL' ? true : false,
    flightFeedTime: flightFromDBById?.flightFeedTime ?? flightFromDBById?.operationInfo?.time ?? null,
    scheduledDate: originalFlightTimeConverted || flightFromDBById?.scheduledDate || scheduledDate,

    flightNumber: flightFromDBById?.flightNumber,
    type: flightFromDBById?.type,
    gate: flightFromDBById?.gate,
    stand: flightFromDBById?.stand,
    terminal: flightFromDBById?.terminal,
    callSign: flightFromDBById?.callSign,
    operatingStatus: flightFromDBById?.operatingStatus,
    aircraftType: flightFromDBById?.aircraftType,
    aircraftRegistration: flightFromDBById?.aircraftRegistration,
    aircraftModel: flightFromDBById?.aircraftModel,
    seats: flightFromDBById?.seats,
    seatCapacity: flightFromDBById?.seatCapacity,
    ambiliftType: flightFromDBById?.ambiliftType,
    portsOfCall: flightFromDBById?.portsOfCall,
    history: flightFromDBById?.history,
    CodeShareFlights: flightFromDBById?.CodeShareFlights ?? [],
    _id: flightFromDBById?._id,
    notes: flightFromDBById?.notes || [],
    manualLock: flightFromDBById?.manualLock ?? false,
    operationInfo: flightFromDBById?.operationInfo ?? null,
    airbridgeInUse: flightFromDBById?.isAirbridgeUsed ?? null,
    thirdStateFlight: isOriginInThirdState,

    // tri state true or false
    triState: true
  };
};

const JobConfirmation = (props) => {

  //#region local states
  const [activeTab, setActiveTab] = useState('assign-passenger');               // active tab in confirmation modal

  const confirmationTabs = [
    { value: 'assign-passenger', label: 'Assign Passenger' },
    { value: 'create-adhoc', label: 'Create Adhoc Task' },
  ];

  const handleTabChange = (tabValue) => {
    if (tabValue === 'create-adhoc' && selectedEmployee) {
      dispatch(setAdhocTaskEmployee(selectedEmployee));
    }
    setActiveTab(tabValue);
  };

  const [selectedName, setSelectedName] = useState('');                         // selected name of the job
  const [flightId, setFlightId] = useState("")                                  // flight id of the PAX
  const [flightTime, setFlightTime] = useState('')                              // flight time of the PAX
  const [flightOperationTime, setFlightOperationTime] = useState('')                // flight operation time of the PAX
  const [origin, setOrigin] = useState('')                                      // destination airport of the PAX
  const [destination, setDestination] = useState('')                            // origin airport of the PAX


  const [clicked, setClicked] = useState([]);                                     // if job is clicked or not
  const [selectedEmployee, setSelectedEmployee] = useState(null)                  // holds value of employee shift end time
  const [selectedDropOff, setSelectedDropOff] = useState(null)                    // drop off location of the PAX
  const [selectedPickUp, setSelectedPickUp] = useState(null)                      // pick up location of the PAX
  const [bookingNotes, setBookingNotes] = useState("")                            // view booking notes     

  const [forMultiplePaxAssign, setForMultiplePaxAssign] = useState(false)             // hold value if the choice is to select multiple pax is turned on or off
  const [multiplePax, setMultiplePax] = useState("")                              // hold value of the multiple pax selected
  const [flightDocument, setFlightDocument] = useState(null);
  const [assignedAsHC, setAssignedAsHC] = useState(false);                      // set task as HC - arrival only
  const [connectionFlightData, setConnectionFlightData] = useState(null);
  const [connectionFlightLoading, setConnectionFlightLoading] = useState(false);
  //#endregion

  //#region redux
  const dispatch = useDispatch();

  const latestJob = useSelector(selectLatestJob);                               // latest job created from the timeline

  const createTaskStatus = useSelector(selectCreateTaskStatus);                 // status of the task creation
  const error = useSelector(selectCreateTaskError);                             // error message from task creation
  const [localError, setLocalError] = useState(null);                             // local error message
  const taskList = useSelector(selectTaskList);                                 // time Line task list
  const selectedTasksList = useSelector(selectSelectedTasksList);               // selected tasks list from the job list        
  const selectedTask = useSelector(selectSelectedTask);                         // selected task from the job list
  const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);   // selected task with multiple pax from the job list
  const selectedMultiplePAXTaskError = useSelector(selectSelectedMultiplePAXTaskError);   // error message from the selected multiple pax task
  const selectedTimeline = useSelector(selectSelectedTimeline);                 // selected timeline from the timeline data
  const updateTaskStatus = useSelector(selectUpdateTaskStatus);                 // update task status 
  const lastTaskQueued = useSelector(selectLastTaskQueued);                     // last create/update was held by the break queue

  const employeeList = useSelector(selectEmployeeList);                         // employee list from the timeline

  const dropOffLocations = useSelector(selectDropOffLocations);                 // airport drop off locations
  const pickUpLocations = useSelector(selectPickUpLocations);                   // airport pick up locations
  const paxCheckInStatus = useSelector(selectPaxCheckInStatus);                 // status of passenger check in 
  const defaultCheckInLocation = useSelector(selectDefaultCheckInLocation)      // default check in location from the timeline data

  const flightList = useSelector(selectFlightListIndexed);                      // list of flights
  const mainFlightDocument = useSelector(selectFlightById(flightId));
  const flightFromDBById = useSelector(selectFlightFromDBById);
  const getFlightByIdStatus = useSelector(selectGetFlightByIdStatus);
  const usedTheme = useSelector(selectUsedTheme);
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);
  const flightsArray = useSelector(selectFlightsArray);
  const clickedEmployeeId = useSelector(selectClickedEmployeeId)
  const thirdStateAirports = useSelector(selectThirdStateAirports);           // list of third state airport iata codes
  // airport
  const dbUser = useSelector(selectFirebaseInfo);
  const firebaseUser = useSelector(selectFirebaseUser);
  const iata = dbUser?.details?.authority;
  //#endregion

  const selectedTaskId = selectedTask?._id ?? null;
  const mainFlightDocId = mainFlightDocument?.flightId ?? mainFlightDocument?._id ?? null;

  //#region useEffect

  // fetch third state airports list once
  useEffect(() => {
      dispatch(getThirdStateAirports(iata));  // fetch third state airports from db
  }, []);

  // set flight document based on main flight document if exists or fetch from db if not
  useEffect(() => {
    dispatch(resetGetFlightFromDBById());
    // Prefer the exact flight document the timeline row / card already resolved.
    // This avoids re-deriving the document from a booking timestamp, which can resolve
    // to the neighbouring day's flight (and wrong gate) for post-midnight arrivals.
    if (selectedTask?.resolvedFlightDocId) {
      dispatch(getFlightById(selectedTask.resolvedFlightDocId));
      return;
    }
    // main flight doc set by selectFlightById (local state)
    if (mainFlightDocument) {
      // exists, used it
      // setFlightDocument(mainFlightDocument);
      dispatch(getFlightById(mainFlightDocument?.flightId ?? mainFlightDocument?._id ?? ""));
    }
    else if (flightId && !mainFlightDocument) {

      // flight does not exist in local state, take flight id and query db for flight document using selectFlightFromDBById (redux state)
      // make sure id matches the id for querying flight by id
      const finalFlightId = checkFetchedFlightId(`${flightId}_IATA_${iata}`, selectedTask);

      if (finalFlightId) {
        // get flight info from server
        dispatch(getFlightById(finalFlightId));
      }

    }
  }, [mainFlightDocId, flightId, selectedTaskId]);

  // set flight document based on flight from db if exists
  useEffect(() => {
    if (!flightFromDBById) return setFlightDocument(null);
    // Guard against stale responses: ignore results that don't match the currently requested flight
    const expectedId = selectedTask?.resolvedFlightDocId
      ?? mainFlightDocument?.flightId ?? mainFlightDocument?._id
      ?? checkFetchedFlightId(`${flightId}_IATA_${iata}`, selectedTask);
    if (flightFromDBById._id !== expectedId) return;

    // build + set the normalised flight document from the DB flight doc
    setFlightDocument(buildFlightDocumentFromDB(flightFromDBById, iata, thirdStateAirports));
  }, [flightFromDBById, mainFlightDocId, thirdStateAirports]);

  // reset assigned as hc each time the task changes
  useEffect(() => {
    setAssignedAsHC(false);
  }, [selectedTask])

  // connection flight lookup: local first, then API fallback
  useEffect(() => {
    const job = selectedTask;
    const onwards = job?.originalBookingDoc?.onwards;
    const inbound = job?.inbound;
    const hasConnection = onwards || inbound || (job?.connection && inbound);

    if (!hasConnection) {
      setConnectionFlightData({ connection: false, connectionFlight: null });
      setConnectionFlightLoading(false);
      return;
    }

    const base = onwards
      ? { ...onwards, type: 'onwards' }
      : { ...inbound, type: 'inbound' };

    const cf = onwards || inbound;
    if (cf?.carrierCode && cf?.flightNumber && cf?.origin) {
      const dest = cf?.destination || '';
      const flightNum = `${cf.carrierCode}${cf.flightNumber}`;
      const key = `${flightNum}_${cf.origin}_${dest}`;
      const localFlight = flightList?.[key];

      if (localFlight) {
        // const time = localFlight?.operationInfo?.time || localFlight?.flightFeedTime || localFlight?.originalFlightTime;
        // if (time) base.scheduledDepartureTime = time;
        // if (localFlight?.gate) base.gate = localFlight.gate;
        // if (localFlight?.stand) base.stand = localFlight.stand;
        // setConnectionFlightData({ connection: true, connectionFlight: base });
        // setConnectionFlightLoading(false);
        // return;
        let cancelled = false;
        setConnectionFlightLoading(true);
        getFlightByIdService(localFlight?._id ?? localFlight?.flightId ?? "")
          .then((data) => {
            if (cancelled) return;
            const match = Array.isArray(data) ? data[0] : data;
            if (match) {
              const time = match?.operationInfo?.time || match?.flightFeedTime || match?.originalFlightTime;
              if (time) base.scheduledDepartureTime = time;
              if (match?.gate) base.gate = match.gate;
              if (match?.stand) base.stand = match.stand;
            }
            setConnectionFlightData({ connection: true, connectionFlight: base });
            setConnectionFlightLoading(false);
          })
          .catch(() => {
            if (cancelled) return;
            setConnectionFlightData({ connection: true, connectionFlight: base });
            setConnectionFlightLoading(false);
          });

        return () => { cancelled = true; };
      }
    }

    if (cf?.carrierCode && cf?.flightNumber && cf?.origin && cf?.departureDate && iata) {
      let cancelled = false;
      setConnectionFlightLoading(true);

      const dest = cf?.destination || '';
      const flightDocId = `${cf.carrierCode}${cf.flightNumber}_${cf.origin}_${dest ? dest + '_' : ''}${cf.departureDate}_IATA_${iata}`;

      getFlightByIdService(flightDocId)
        .then((data) => {
          if (cancelled) return;
          const match = Array.isArray(data) ? data[0] : data;
          if (match) {
            const time = match?.operationInfo?.time || match?.flightFeedTime || match?.originalFlightTime;
            if (time) base.scheduledDepartureTime = time;
            if (match?.gate) base.gate = match.gate;
            if (match?.stand) base.stand = match.stand;
          }
          setConnectionFlightData({ connection: true, connectionFlight: base });
          setConnectionFlightLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setConnectionFlightData({ connection: true, connectionFlight: base });
          setConnectionFlightLoading(false);
        });

      return () => { cancelled = true; };
    }

    setConnectionFlightData({ connection: true, connectionFlight: base });
    setConnectionFlightLoading(false);
  }, [selectedTask, flightList]);

  // on render clear / reset redux state
  useEffect(() => {
    dispatch(clearCreateTaskStatus());
    dispatch(resetUpdateTaskFlags());
    dispatch(resetGetFlightFromDBById());

    // auto set first task in selected tasks as the selected task
    // if there is no selected task and the selected task list is not empty array set the selected task to the first element in selectedTasksList array
    if (Object.isObjectEmpty(selectedTask) && Array.isFullArray(selectedTasksList)) {

      let tempSelectedTask = selectedTasksList?.[0] ?? null;

      // check task is not empty before setting it as the selected task + clicked task
      if (!Object.isObjectEmpty(tempSelectedTask)) {
        dispatch(setSelectedTask(tempSelectedTask));
        setClicked(tempSelectedTask?.["_id"] ?? null);
      }
    };
  }, []);

  // set selected employee based on clicked column
  useEffect(() => {
    if (props?.clickedColumn) {
      let employeeId = props?.clickedColumn?.split("-")?.[1]
      if (employeeId) setSelectedEmployee(employeeList.find((employee) => employee.employeeId === employeeId))
    }
  }, [props?.clickedColumn]);

  // use flight doc to set flight time
  useEffect(() => {
    if (flightDocument) {
      // extract flight time
      if (flightDocument?.operationTime) {
        let time = flightDocument?.operationTime;
        time = new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setFlightTime(time)
        setFlightOperationTime(flightDocument?.operationTime)
      }


      // extract origin / destination airport
      if (flightDocument?.origin && flightDocument?.destination) {
        setOrigin(flightDocument?.origin)
        setDestination(flightDocument?.destination)
      }

    }
  }, [flightDocument])

  // set state info based on selected task details
  useEffect(() => {
    dispatch(resetGetFlightFromDBById());

    // check if selected task exists
    if (Object.isObjectEmpty(selectedTask)) {

      // reset states
      setBookingNotes("")
      setFlightTime('')
      setFlightOperationTime('')
      return;
    } else {
      // selected task exists, set states based on selected task details
      setSelectedPickUp(null);
      setSelectedDropOff(null);
      setFlightDocument(null);
      // Pre-populate origin/destination immediately from booking data so the summary
      // text doesn't show "( - )" while waiting for the async flight fetch
      setOrigin(
        selectedTask?.flightConfirmationDetails?.origin ??
        selectedTask?.flightInformation?.departureAirport ?? ''
      );
      setDestination(
        selectedTask?.flightConfirmationDetails?.destination ??
        selectedTask?.flightInformation?.arrivalAirport ?? ''
      );

      //#region set selected name
      if (selectedTask?.combinedMarshallBooking) {
        setSelectedName('Marshall Task')
      } else if (selectedTask?.jobType == 'ramp-task') {
        setSelectedName('Ramp Task')
      } else if (selectedTask?.combinedBooking || selectedTask?.isBoardingBooking) {
        setSelectedName('Boarding Task')
      }
      else if (selectedTask?.passengerInformation?.title) {
        setSelectedName(processName(selectedTask?.passengerInformation?.title));
      }
      else {
        setSelectedName(selectedTask?.name ?? "n/a");
      }
      //#endregion

      //#region set notes based on selected task booking notes value
      if (selectedTask?.flightBooking) {
        setBookingNotes(selectedTask?.originalBookingDoc?.notes ?? "")
      }
      else {
        setBookingNotes(selectedTask?.notes ?? "")
      }
      //#endregion

      //#region check if job or task for flight number / flight doc id
      if (selectedTask?.flightConfirmationDetails?.flightNumber && selectedTask?.flightConfirmationDetails?.carrierCode) {

        // its a job, need to use flight confirmation details
        let carrierCode = selectedTask?.flightConfirmationDetails?.carrierCode ?? ""

        // create flight id using origin / destination

        const flightKeyDate = iata == selectedTask?.flightConfirmationDetails?.origin
          ? selectedTask?.flightConfirmationDetails?.departureDate
          : selectedTask?.flightConfirmationDetails?.arrivalTime
            ? new Date(selectedTask.flightConfirmationDetails.arrivalTime).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) })
            : selectedTask?.flightConfirmationDetails?.departureDate;
        let flightId = carrierCode + selectedTask?.flightConfirmationDetails?.flightNumber + "_" + selectedTask?.flightConfirmationDetails?.origin + "_" + selectedTask?.flightConfirmationDetails?.destination + '_' + flightKeyDate

        // detect overnight delay: flight originally scheduled on day X but delayed to day Y
        // use first realtime_history 'from' date as the key so we match the original flight document
        if (iata === selectedTask?.flightConfirmationDetails?.destination) {
          const realtimeHistory = selectedTask?.originalBookingDoc?.realtime_history ?? selectedTask?.realtime_history ?? [];
          if (realtimeHistory.length > 0) {
            const firstFrom = realtimeHistory[0]?.from;
            const lastTo = realtimeHistory[realtimeHistory.length - 1]?.to;
            if (firstFrom && lastTo) {
              const firstFromDate = new Date(firstFrom).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });
              const lastToDate = new Date(lastTo).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });
              const arrivalDate = selectedTask?.flightConfirmationDetails?.arrivalTime
                ? new Date(selectedTask.flightConfirmationDetails.arrivalTime).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) })
                : null;
              if (firstFromDate !== flightKeyDate && lastToDate !== firstFromDate && arrivalDate && arrivalDate !== firstFromDate) {
                flightId = carrierCode + selectedTask?.flightConfirmationDetails?.flightNumber + "_" + selectedTask?.flightConfirmationDetails?.origin + "_" + selectedTask?.flightConfirmationDetails?.destination + '_' + firstFromDate;
              }
            }
          }
        }

        setFlightId(flightId);

      }
      else if (selectedTask.flightInformation && selectedTask.flightInformation.flightNumber) {

        // its a task, need to use flight information
        let flightNumber = selectedTask.flightInformation.flightNumber;

        // create flight id using origin / destination
        let taskDepartureDate = selectedTask?.flightInformation?.departureDate || selectedTask?.flightInformation?.flightDate;

        if (!taskDepartureDate) {
          taskDepartureDate = selectedTask?.flightInformation?.departureAirport === iata
            ? selectedTask?.flightInformation?.date
            : selectedTask?.flightInformation?.operationInfo?.time
              ? new Date(selectedTask.flightInformation.operationInfo.time).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) })
              : selectedTask?.flightInformation?.date;
        }
        let flightId = flightNumber + "_" + selectedTask.flightInformation.departureAirport + "_" + selectedTask.flightInformation.arrivalAirport + '_' + taskDepartureDate

        setFlightId(flightId);

      }
      else if (selectedTask?.flightKey) {
        setFlightId(selectedTask?.flightKey?.replace("_BOARDING", "") ?? "")
      }
      else {
        setFlightId("")
        // set error 
        setLocalError("No flight information found for the selected task.")
      }
      //#endregion

    }


  }, [selectedTaskId]);

  // MOVE TO BACKEND!!!!!!
  // send notification to employee if task is successfully created or updated
  useEffect(() => {
    if (createTaskStatus == "succeeded" || updateTaskStatus == "succeeded") {

      dispatch(setToast({
        message: lastTaskQueued ? 'Agent is on break: task queued until the break is finished' : 'Successfully allocated task',
        statusType: 'success',
      }));
      dispatch(setShowToast(true));

      // close modal etc.
      props.taskConfirmed();

      // find selected employee
      let selectedEmployee = null;
      selectedEmployee = employeeList.find((employee) => employee.employeeId === latestJob?.employeeId);

      // send notification to employee if fcm token exsists (a queued task notifies when the break ends)
      if (selectedEmployee?.FCMTokens && !lastTaskQueued) {
        let notification = {
          notificationTitle: "",
          notificationText: "",
          fcmToken: selectedEmployee?.FCMTokens,
          iata
        };
        if (assignedAsHC) {
          notification.notificationTitle = "Assigned WCHC Chair Lift Task";
          notification.notificationText = "Please service WCHC passenger - lifting equipment may be required"
        }
        else {
          notification.notificationTitle = "New Assigned Task at " + latestJob?.assignedTime;
          notification.notificationText = `Please collect passenger ${latestJob?.name ? latestJob?.name : ""}`;
        }

        dispatch(sendEmployeeNotification(notification));
      }

      // reset flags
      resetPage();
    }
  }, [createTaskStatus, latestJob, updateTaskStatus, lastTaskQueued]);

  // reset pax checkin flags when paxCheckInStatus is succeeded or failed
  useEffect(() => {
    if (paxCheckInStatus == "succeeded" || paxCheckInStatus == "failed") {
      dispatch(resetPaxCheckInFlags());
    }
  }, [paxCheckInStatus]);

  // auto-set assignedAsHC based on selected task WCHC status (single pax only)
  useEffect(() => {
    if (forMultiplePaxAssign) return; // multi-pax: let user decide
    const codes = ['WCHC'];
    const isWCHC = selectedTask?.assistance?.some(str =>
      codes.some(code => String(str)?.toUpperCase()?.includes(code))
    );
    setAssignedAsHC(!!isWCHC);
  }, [selectedTask, forMultiplePaxAssign]);

  //#endregion

  //#region functions
  const getEmployeeById = (employeeId) => {
    return employeeList?.find((employee) => {
      return employee?.employeeId === employeeId
        || employee?.uid === employeeId
        || employee?.userId === employeeId
        || employee?._id === employeeId;
    });
  };

  const getVersionedEmployeeStatus = (employeeId, status) => {
    const employee = getEmployeeById(employeeId) ?? selectedEmployee;
    return getEmployeeStatusForAppVersion(status, employee?.appVersionNumber);
  };

  const updateEmployeeStatusForAppVersion = (employeeId, status) => {
    const versionedStatus = getVersionedEmployeeStatus(employeeId, status);

    if (!versionedStatus) return;

    const empPayload = {
      userId: employeeId,
      body: {
        status: versionedStatus
      }
    };

    dispatch(updateEmployee(empPayload));
  };

  const checkFetchedFlightId = (FID, wantedTask) => {
    let finalFlightId = "";

    if (!wantedTask) return FID;
    // Prefer the flight document the timeline row already resolved for this task,
    // so the confirmation uses the exact same document as the card/timeline.
    if (wantedTask?.resolvedFlightDocId) return wantedTask.resolvedFlightDocId;
    //#region check if job or task for flight number / flight doc id
    if (wantedTask?.flightConfirmationDetails?.flightNumber && wantedTask?.flightConfirmationDetails?.carrierCode) {

      // its a job, need to use flight confirmation details
      let carrierCode = wantedTask?.flightConfirmationDetails?.carrierCode ?? ""

      // create flight id using origin / destination

      const flightKeyDate = iata == wantedTask?.flightConfirmationDetails?.origin
        ? wantedTask?.flightConfirmationDetails?.departureDate
        : wantedTask?.flightConfirmationDetails?.arrivalTime
          ? new Date(wantedTask.flightConfirmationDetails.arrivalTime).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) })
          : wantedTask?.flightConfirmationDetails?.departureDate;
      let flightId = carrierCode + wantedTask?.flightConfirmationDetails?.flightNumber + "_" + wantedTask?.flightConfirmationDetails?.origin + "_" + wantedTask?.flightConfirmationDetails?.destination + '_' + flightKeyDate

      if (iata === wantedTask?.flightConfirmationDetails?.destination) {
        const realtimeHistory = wantedTask?.originalBookingDoc?.realtime_history ?? wantedTask?.realtime_history ?? [];
        if (realtimeHistory.length > 0) {
          const firstFrom = realtimeHistory[0]?.from;
          const lastTo = realtimeHistory[realtimeHistory.length - 1]?.to;
          if (firstFrom && lastTo) {
            const firstFromDate = new Date(firstFrom).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });
            const lastToDate = new Date(lastTo).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });
            const arrivalDate = wantedTask?.flightConfirmationDetails?.arrivalTime
              ? new Date(wantedTask.flightConfirmationDetails.arrivalTime).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) })
              : null;
            if (firstFromDate !== flightKeyDate && lastToDate !== firstFromDate && arrivalDate && arrivalDate !== firstFromDate) {
              flightId = carrierCode + wantedTask?.flightConfirmationDetails?.flightNumber + "_" + wantedTask?.flightConfirmationDetails?.origin + "_" + wantedTask?.flightConfirmationDetails?.destination + '_' + firstFromDate;
            }
          }
        }
      }

      finalFlightId = flightId;

    }
    else if (wantedTask.flightInformation && wantedTask.flightInformation.flightNumber) {

      // its a task, need to use flight information
      let flightNumber = wantedTask.flightInformation.flightNumber;

      // create flight id using origin / destination
      let taskDepartureDate = wantedTask?.flightInformation?.departureDate || wantedTask?.flightInformation?.flightDate;

      if (!taskDepartureDate) {
        taskDepartureDate = wantedTask?.flightInformation?.departureAirport === iata
          ? wantedTask?.flightInformation?.date
          : wantedTask?.flightInformation?.operationInfo?.time
            ? new Date(wantedTask.flightInformation.operationInfo.time).toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) })
            : wantedTask?.flightInformation?.date;
      }
      let flightId = flightNumber + "_" + wantedTask.flightInformation.departureAirport + "_" + wantedTask.flightInformation.arrivalAirport + '_' + taskDepartureDate
      finalFlightId = flightId;

    }
    else if (wantedTask?.flightKey) {
      finalFlightId = wantedTask?.flightKey?.replace("_BOARDING", "") ?? "";
    }
    else {
      finalFlightId = "";
    }

    // if the final flight id is not the same as the fetched flight id, return the final flight id
    if (finalFlightId !== FID) {
      return `${finalFlightId}_IATA_${iata}`;
    }
    return FID;
  }


  // handle confirmation of task assignment and creating task in firestore
  const confirm = async () => {

    let taskFlightDocument = flightDocument;

    if (!taskFlightDocument) {
      const fid = selectedTask?.resolvedFlightDocId
        ?? mainFlightDocument?.flightId
        ?? checkFetchedFlightId(`${flightId}_IATA_${iata}`, selectedTask);
      if (fid) {
        const data = await getFlightByIdService(fid).catch(() => null);
        const raw = Array.isArray(data) ? data[0] : data;
        taskFlightDocument = buildFlightDocumentFromDB(raw, iata, thirdStateAirports)
          ?? buildFlightDocumentFromDB(mainFlightDocument, iata, thirdStateAirports)
          ?? mainFlightDocument
          ?? null;
      }
    }

    // get the selected task from the selected task list or the selected task
    const confirmedTask = selectedTask || selectedTasksList[0]

    // find employee id and column using clicked column
    let column = props?.clickedColumn?.split("-")[0];
    let employeeId = props?.clickedColumn?.split("-")[1];
    // create job object using selected task and column
    let job = {
      ...confirmedTask,
      assignedTime: column,
      employeeId: employeeId,
    };


    // get the selected job flight number
    const toAssignFlightNumber = job?.flightConfirmationDetails
      ? `${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}`
      : job?.flightInformation?.flightNumber;

    // compare the flight number to the flight document flight number
    if (toAssignFlightNumber && toAssignFlightNumber !== taskFlightDocument?.flightNumber) {
      taskFlightDocument = null;
    }


    console.log('job:', job);

    // add the task to the timeline
    dispatch(setTimelineData({ job }));

    // if selected task isALTPickUp it already exists in the tasklist so it only needs to be updated 
    // also for gate relocation tasks
    if (confirmedTask?.isALTPickUp || confirmedTask?.isGateRelocation) {

      // find the alt pickup task in the task list to update it
      let tempALTPickUp = taskList?.find((taskItem) => taskItem?._id === confirmedTask?._id);
      let altTaskNotes = [];

      if (tempALTPickUp) {
        let taskUpdates = {
          id: tempALTPickUp._id,
          assignedTime: column,
          employeeId: employeeId,
          pickUpLocation: formatPickUpLocation(selectedPickUp),
          dropOffLocation: formatDropOffLocation(selectedDropOff),
          pending: false,
          notes: job.notes ?? bookingNotes,
          paxPositionInfo: {
            activity: "Assigned",
            locationId: formatPickUpLocation(selectedPickUp)?.id ?? "",
            locationName: formatPickUpLocation(selectedPickUp)?.name ?? "",
            time: new Date(),
            employeeId: employeeId ?? null,
            employeeName: employeeList?.find((e) => e?.employeeId === employeeId)?.title || employeeList?.find((e) => e?.employeeId === employeeId)?.name || null,
          },
          selectALTPickUpPaxStatus: "selected",
          assignedAsHC: assignedAsHC
        };

        if (Array.isFullArray(job?.taskNotes)) {
          altTaskNotes = [...job?.taskNotes];
        }
        if (Array.isFullArray(bookingNotes)) {
          altTaskNotes = [...altTaskNotes, ...bookingNotes];
        }

        if (Array.isFullArray(altTaskNotes)) {
          taskUpdates["taskNotes"] = altTaskNotes;
        }

        dispatch(updateTask(taskUpdates)).then((updateRes) => {
          // a task held for an agent on break leaves them on break
          if (clickedEmployeeId && !updateRes.payload?.queued) updateEmployeeStatusForAppVersion(clickedEmployeeId, "available");
        });

      };

    }
    // task is not a pick up task so create a new task in the backend
    else {

      // create object to be dispatched to backend
      let taskObject = {};

      console.log('taskFlightDocument', taskFlightDocument);

      // get the flight time to assign from job 
      const toAssignFlightTime = job?.flightConfirmationDetails?.destination == iata
        ? job?.flightConfirmationDetails?.arrivalTime
        : job?.flightConfirmationDetails?.departureTime;

      // get the flight date to assign from job 
      const toAssignFlightDate = job?.flightConfirmationDetails?.destination == iata
        ? job?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0]
        : job?.flightConfirmationDetails?.departureDate ?? job?.flightConfirmationDetails?.departureTime?.split("T")?.[0];

      // get the origin to assign from taskFlightDocument or job
      const toAssignOrigin = taskFlightDocument
        ? origin
        : job?.flightConfirmationDetails?.origin ?? job?.flightInformation?.departureAirport;

      // get the destination to assign from taskFlightDocument or job
      const toAssignDestination = taskFlightDocument
        ? destination
        : job?.flightConfirmationDetails?.destination ?? job?.flightInformation?.arrivalAirport;

      // Firestore rejects undefined fields outright - if the flight route couldn't be resolved
      // (flight document still loading and no fallback data on the job), stop here instead of
      // letting setDoc throw on flightInformation.arrivalAirport/departureAirport.
      if (!toAssignOrigin || !toAssignDestination) {
        setLocalError("Missing flight route information. Please close and reopen this assignment, or contact the development team if that still happens.");
        return;
      }

      const toAssignArrivalTime = job?.flightConfirmationDetails?.destination == iata
        ? taskFlightDocument?.operationTime ?? flightOperationTime ?? job?.flightConfirmationDetails?.arrivalTime ?? toAssignFlightTime ?? null
        : job?.flightConfirmationDetails?.arrivalTime ?? toAssignFlightTime ?? null;

      const toAssignDepartureTime = job?.flightConfirmationDetails?.origin == iata
        ? taskFlightDocument?.operationTime ?? flightOperationTime ?? job?.flightConfirmationDetails?.departureTime ?? toAssignFlightTime ?? null
        : job?.flightConfirmationDetails?.departureTime ?? toAssignFlightTime ?? null;

      taskObject = {
        active: false,
        completed: false,
        acknowledged: false,
        employeeId: job.employeeId,
        ...(clickedEmployeeId && { uid: clickedEmployeeId }),//auto-generated firebase, old account with custom employeeId, not valid to update firestore doc

        assignedTime: job.assignedTime,
        flightId: taskFlightDocument?.flightId ?? `${flightId}_IATA_${iata}` ?? null,
        flightInformation: {
          iata: iata,
          arrivalAirport: toAssignDestination,
          departureAirport: toAssignOrigin,
          flightNumber: taskFlightDocument?.flightNumber ?? toAssignFlightNumber ?? "",
          flightTime: taskFlightDocument?.operationTime ?? taskFlightDocument?.operationInfo?.time ?? flightOperationTime ?? toAssignFlightTime ?? "",
          flightDate: taskFlightDocument?.flightDate ?? toAssignFlightDate ?? "",
          arrivalTime: toAssignArrivalTime || job?.flightConfirmationDetails?.arrivalTime || flightOperationTime || taskFlightDocument?.operationTime || null,
          departureTime: toAssignDepartureTime || job?.flightConfirmationDetails?.departureTime || flightOperationTime || taskFlightDocument?.operationTime || null,
          departureDate: job?.flightConfirmationDetails?.departureDate || taskFlightDocument?.scheduledDate || taskFlightDocument?.flightDate || taskFlightDocument?.date || null,
          seatNumber: getSeatNumber(job),
          // Aircraft information
          aircraftType: taskFlightDocument?.aircraftType || null,
          aircraftRegistration: taskFlightDocument?.aircraftRegistration || null,
          aircraftModel: taskFlightDocument?.aircraftModel || null,
          seats: taskFlightDocument?.seats || null,
          seatCapacity: taskFlightDocument?.seatCapacity || null,
          ambiliftType: taskFlightDocument?.ambiliftType || null,
          portsOfCall: taskFlightDocument?.portsOfCall || null,
          originalFlightTime: taskFlightDocument?.scheduled || taskFlightDocument?.originalFlightTime || taskFlightDocument?.operationTime || taskFlightDocument?.operationInfo?.time || toAssignFlightTime || null,
          thirdStateFlight: taskFlightDocument?.thirdStateFlight ? taskFlightDocument?.thirdStateFlight : false,
          scheduledDate: taskFlightDocument?.scheduledDate || taskFlightDocument?.flightDate || taskFlightDocument?.date || toAssignFlightDate || null,
        },
        passengerInformation: {
          assistance: job.assistance ? job?.assistance : [],
          title: job.name ? job.name : "n/a",
        },
        flightBooking: job.flightBooking ? job.flightBooking : false,
        allocatedTime: new Date(),
        _id: job?._id?.includes("-duplicate-") ? job?._id : `${job?._id}_IATA_${iata}`,
        cancelled: false,
        pending: false,
        selectedTimeline: selectedTimeline?._id ?? null,
        dropOffLocation: formatDropOffLocation(selectedDropOff),
        allocatedDropOffLocation: formatDropOffLocation(selectedDropOff), // add the main allocated task drop- off,
        pickUpLocation: formatPickUpLocation(selectedPickUp),
        allocatedPickUpLocation: formatPickUpLocation(selectedPickUp), // add the main allocated task pick-up,
        isTransitBooking: job?.isTransitBooking ? true : false,
        notPlanned: false,
        isEstimatedBooking: job.isEstimatedBooking ? true : false,
        passengerDutTime: job.passengerDutTime ? job.passengerDutTime : null,
        combinedBooking: job.combinedBooking || job.isBoardingBooking ? true : false,                // boarding booking
        isBoardingBooking: job.isBoardingBooking ? true : false,                                     // boarding booking       
        passengers: Array.isFullArray(job.passengers) ? job.passengers.map((passenger) => {
          return {
            assistance: passenger.assistance ?? [],
            name: passenger.originalBookingDoc?.name ?? passenger.name ?? "n/a",
            originalBookingId: passenger.originalBookingId ?? passenger._id ?? null,
            bookingCreateDate: passenger.originalBookingDoc?.dateCreated ?? passenger.dateCreated ?? null,
          }
        }) : [],
        bookingCreateDate: job.dateCreated ?? null,
        notes: job.notes ?? bookingNotes,
        requireUpdates: job?.notComplete ?? job?.originalBookingDoc?.notComplete ?? false,
        assignedAsHC: assignedAsHC,
        assignedBy: {
          id: firebaseUser?.uid ?? "",
          name: firebaseUser?.displayName ?? "",
          from: "portal",
          time: new Date(),
        },
        ...(connectionFlightData || { connection: false, connectionFlight: null }),
      };

      if (job?._id?.includes("-part") && origin == iata) {
        taskObject["duplicatedBookingTask"] = true;
      }

      //#region new notes added to task
      const bookingNoteEntries = normalizeBookingNotes(bookingNotes);
      if (bookingNoteEntries.length > 0
        //  && iata !== "JER"
      ) {
        taskObject["taskNotes"] = bookingNoteEntries;
      }
      if ((taskObject?.combinedBooking || taskObject?.isBoardingBooking) && Array.isFullArray(taskObject?.passengers)
        //  && iata !== "JER"
      ) {

        taskObject.passengers = taskObject.passengers.map((pax) => {
          const mainPaxObj = job?.passengers?.find((jobPax) => jobPax?._id === pax?.originalBookingId);
          // Search for a task where task._id includes the passenger's originalBookingId
          const matchingTasks = taskList?.filter((task) => {
            const originalBookingId = pax?.originalBookingId;
            return originalBookingId && (task?._id?.includes(originalBookingId) || (task?.isALTPickUp && task?.originalALTPaxId === originalBookingId));
          }) ?? [];
          const allTaskNotes = matchingTasks.flatMap((task) => (
            Array.isFullArray(task?.taskNotes) ? task.taskNotes : []
          ));
          // Merge booking-level notes with task notes so notes stored on the
          // booking doc (notes) aren't dropped when a matching task exists.
          // normalizeAndMergePaxNotes dedupes on userId::text.
          const paxNotes = normalizeAndMergePaxNotes([
            ...normalizeBookingNotes(mainPaxObj?.notes),
            ...allTaskNotes,
          ]);
          return {
            ...pax,
            paxNotes: paxNotes
          };
        });

      }
      if (taskObject?.combinedBooking || taskObject?.isBoardingBooking) {
        let tempTime = taskFlightDocument?.operatingTimes?.ScheduledDateTime || taskFlightDocument?.originalFlightTime || taskFlightDocument?.operationInfo?.time;
        // const taskBoardingTime = taskFlightDocument?.boardingTime
        //   ? taskFlightDocument?.boardingTime
        //   : new Date(new Date(taskFlightDocument?.operationTime ?? toAssignFlightTime) - 45 * 60 * 1000).toISOString();
        const taskBoardingTime = getBoardingTime(
          taskFlightDocument,
          tempTime,
          taskFlightDocument?.seats,
          iata
        );

        const boardingFallbackTime = taskFlightDocument?.operationTime ?? toAssignFlightTime ?? null;
        taskObject["flightInformation"]["boardingTime"] = taskBoardingTime
          ? taskBoardingTime
          : boardingFallbackTime
            ? new Date(new Date(boardingFallbackTime) - 45 * 60 * 1000).toISOString()
            : null;
      }
      //#endregion

      // check if job is a ramp task
      if (job?.combinedRampBooking) taskObject["combinedRampBooking"] = true;
      if (job?.combinedMarshallBooking) taskObject["combinedMarshallBooking"] = true;

      // check if job has flightBooking it's arrival or if it has isEstimatedBooking it Estimated departure add originalBookingId to the task for edit booking
      if (job?.flightBooking || job?.isEstimatedBooking) taskObject["originalBookingId"] = job?.originalBookingId ?? null;
      if (job?.flightBooking) taskObject["bookingCreateDate"] = job?.originalBookingDoc?.dateCreated ?? null;
      // if (!job?.combinedBooking && !job?.combinedRampBooking) taskObject["_id"] = `${job?._id}_IATA_${iata}`;

      // check if selectedTask has paxPositionInfo it's a checked in passenger booking add the checkInTime to task
      if (!Object.isObjectEmpty(confirmedTask?.paxPositionInfo)) { taskObject["checkInTime"] = confirmedTask?.paxPositionInfo?.time }

      // add gate information to departure flight
      if (job.gateInformation) {
        taskObject["flightInformation"] = {
          ...taskObject.flightInformation,
          gate: job.gateInformation.gate,
          stand: job.gateInformation.stand,
          terminalTitle: job.gateInformation.terminal,
        }
      }

      // add gate information to arrival flight
      if (job.flightBooking && job.originalBookingDoc && job.originalBookingDoc.gateInformation) {
        taskObject["flightInformation"] = {
          ...taskObject.flightInformation,
          gate: job.originalBookingDoc.gateInformation.gate,
          stand: job.originalBookingDoc.gateInformation.stand,
          terminalTitle: job.originalBookingDoc.gateInformation.terminal,
        }
      }

      // add gate + stand + terminal info to task 
      // try to find the flight number in the flight list and add the gate information to the task object
      if (taskFlightDocument) {
        taskObject["flightInformation"] = {
          ...taskObject.flightInformation,
          gate: taskFlightDocument.gate ?? "N/A",
          stand: taskFlightDocument.stand ?? "N/A",
          terminalTitle: taskFlightDocument.terminal ?? "N/A",
          // flightTime: flightOperationTime ?? taskFlightDocument?.operationTime ?? toAssignFlightTime ?? ""
        }
      }


      //taskObject["flightInformation"]["flightTime"] = "";
      if (!taskObject?.flightInformation?.flightTime) {
        taskObject["flightInformation"]["flightTime"] = origin == iata
          ? job?.flightConfirmationDetails?.departureTime
          : job?.flightConfirmationDetails?.arrivalTime;
      }
      if (job.ambiliftRequired) taskObject["ambiliftRequired"] = true;

      console.log('taskObject:', taskObject);
      // save task to firestore
      const res = await dispatch(createTask(taskObject));

      // // check response from createTask is successful change the paxStatus in paxPositionInfo to assigned
      if (res?.meta?.requestStatus === "fulfilled") {
        // Clean up client-side duplicate from persistence if this was a locally-created duplicate
        if (job?.isClientDuplicate && job?._id) {
          dispatch(removeClientDuplicate(job._id));
        }

        if (dbUser.details.authority !== "JER") {
          handleSelectPassengers(confirmedTask, job);
          handleCreateFlightConversation(job);
        }
        if (clickedEmployeeId && !res.payload.queued) updateEmployeeStatusForAppVersion(clickedEmployeeId, "available");
        if ((!Object.isObjectEmpty(confirmedTask?.paxPositionInfo) || selectedPickUp) && isNormalTask(confirmedTask)) {
          // update booking location status 
          const assignedEmployee = employeeList?.find((e) => e?.employeeId === job?.employeeId);
          const employeeName = assignedEmployee?.title || assignedEmployee?.name || null;

          let object = {
            bookingId: confirmedTask.flightBooking ? confirmedTask.originalBookingId : confirmedTask?._id,
            location: confirmedTask?.paxPositionInfo?.locationId ? confirmedTask?.paxPositionInfo?.locationId : formatPickUpLocation(selectedPickUp)?.id,
            locationTitle: confirmedTask?.paxPositionInfo?.locationTitle ? confirmedTask?.paxPositionInfo?.locationTitle : formatPickUpLocation(selectedPickUp)?.name,
            activity: "Assigned",
            time: new Date(),
            checkInFlag: false,
            employeeId: job?.employeeId ?? null,
            employeeName,
            setBy: {
              id: firebaseUser?.uid ?? "",
              name: firebaseUser?.displayName ?? "",
              from: "portal",
              time: new Date(),
            }
          }
          dispatch(setBookingLocation(object))

        }
      }
    };
  };

  // function to handle selecting passengers for a confirmed task to mark the pax by the task type 
  const handleSelectPassengers = (confirmedTask, selectedJobToAssign) => {
    let payload = null;
    // if the selectedJobToAssign is departure(preBoard) and not combined boarding, duplicated or ramp
    if (origin == iata && !selectedJobToAssign?.combinedBooking && !selectedJobToAssign?.combinedRampBooking && !selectedJobToAssign?._id?.includes("_part-") && !selectedJobToAssign?._id?.includes("-part")) {
      payload = {
        taskId: confirmedTask?._id,
        body: {
          bookingsIds: [selectedJobToAssign?._id],
          taskType: "departure",
          toAssign: true,
          iata: dbUser.details.authority

        }

      };
    } else if (selectedJobToAssign?.combinedBooking && Array.isFullArray(selectedJobToAssign?.passengers)) {
      // if the selectedJobToAssign is combined boarding
      // payload = {
      //   taskId: confirmedTask?._id,
      //   body: {
      //     bookingsIds: selectedJobToAssign?.passengers?.map((pax) => pax?._id),
      //     taskType: "combinedBooking",
      //     toAssign: true,
      //     iata: dbUser.details.authority

      //   }

      // };
      payload = null;
    } else if (selectedJobToAssign?.combinedRampBooking && Array.isFullArray(selectedJobToAssign?.passengers)) {
      payload = {
        taskId: confirmedTask?._id,
        body: {
          bookingsIds: selectedJobToAssign?.passengers?.map((pax) => pax?._id || pax?.id),
          taskType: "combinedRampBooking",
          toAssign: true,
          iata: dbUser.details.authority

        }
      };
    };
    if (payload) {
      dispatch(selectPassengers(payload));
    }
  }

  const handleCreateFlightConversation = (selectedJobToAssign) => {
    let wantedFlight = null;
    if (flightDocument) {
      wantedFlight = flightDocument;
    }

    // check if flight has landed or departed  based on time 
    let isFlightDisabled = false;
    if (wantedFlight) {
      const minusTime = wantedFlight?.type === "departure"
        ? 1000 * 60 * 5  // 5 minutes for departures
        : 1000 * 60 * 60; // 1 hour for arrivals
      const currentTime = new Date(new Date().getTime() - minusTime);
      const operationTime = new Date(wantedFlight?.operationTime);
      if (currentTime > operationTime) {
        isFlightDisabled = true;
      }

    } else {
      const flightDate = selectedJobToAssign?.flightConfirmationDetails?.origin == iata
        ? selectedJobToAssign?.flightConfirmationDetails?.departureDate
        : selectedJobToAssign?.flightConfirmationDetails?.arrivalTime ? selectedJobToAssign?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0] : selectedJobToAssign?.flightConfirmationDetails?.departureDate
      const minusTime = selectedJobToAssign?.flightConfirmationDetails?.origin == iata
        ? 1000 * 60 * 5  // 5 minutes for departures
        : 1000 * 60 * 60; // 1 hour for arrivals
      const currentTime = new Date(new Date().getTime() - minusTime);
      const operationTime = new Date(flightDate);
      if (currentTime > operationTime) {
        isFlightDisabled = true;
      }
    }
    if (!isFlightDisabled) {
      let conversationPayload = null;
      if (!Object.isObjectEmpty(wantedFlight)) {
        let originalFlightId = wantedFlight?._id ?? wantedFlight?.originalFlightId ?? `${wantedFlight?.bookingKey}_IATA_${iata}`;
        if (originalFlightId?.includes("_BOARDING")) originalFlightId = originalFlightId.replace("_BOARDING", "");
        conversationPayload = {
          iata: iata,
          body: {
            flightId: originalFlightId,
            flightNumber: wantedFlight?.flightNumber,
            createdBy: firebaseUser?.uid ?? "",
            flightDate: wantedFlight?.operationTime?.split("T")?.[0] ?? new Date()?.toISOString()?.split("T")?.[0],
            participants: [
              {
                id: firebaseUser?.uid ?? "",
                name: firebaseUser?.displayName ?? "",
                type: "portal",
              },
              {
                id: selectedEmployee?.employeeId ?? "",
                name: selectedEmployee?.title ?? "",
                type: "app",
              }
            ]
          }
        }

      } else {
        const flightDate = selectedJobToAssign?.flightConfirmationDetails?.origin == iata
          ? selectedJobToAssign?.flightConfirmationDetails?.departureDate
          : selectedJobToAssign?.flightConfirmationDetails?.arrivalTime ? selectedJobToAssign?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0] : selectedJobToAssign?.flightConfirmationDetails?.departureDate
        const flightId = `${selectedJobToAssign?.flightConfirmationDetails?.carrierCode}${selectedJobToAssign?.flightConfirmationDetails?.flightNumber}_${selectedJobToAssign?.flightConfirmationDetails?.origin}_${selectedJobToAssign?.flightConfirmationDetails?.destination}_${flightDate ? flightDate : new Date()?.toISOString()?.split("T")?.[0]}_IATA_${iata}`;
        conversationPayload = {
          iata: iata,
          body: {
            flightId: flightId,
            flightNumber: `${selectedJobToAssign?.flightConfirmationDetails?.carrierCode}${selectedJobToAssign?.flightConfirmationDetails?.flightNumber}`,
            createdBy: firebaseUser?.uid ?? "",
            flightDate: flightDate,
            participants: [
              {
                id: firebaseUser?.uid ?? "",
                name: firebaseUser?.displayName ?? "",
                type: "portal",
              },
              {
                id: selectedEmployee?.employeeId ?? "",
                name: selectedEmployee?.title ?? "",
                type: "app",
              }
            ]
          }
        }
      }
      dispatch(createFlightConversation(conversationPayload))
    }
  }

  const handleMultiplePaxAssign = async () => {
    // get the selected task from the selected task list or the selected task
    const confirmedTask = selectedMultiplePAXTask?.[0]

    // find employee id and column using clicked column
    let column = props?.clickedColumn?.split("-")[0];
    let employeeId = props?.clickedColumn?.split("-")[1];

    let job = {
      ...confirmedTask,
      assignedTime: column,
      employeeId: employeeId,
    };

    let taskObject = {};
    const flightDate = job?.flightConfirmationDetails?.destination == iata && job?.flightConfirmationDetails?.arrivalTime
      ? job?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0]
      : job?.flightConfirmationDetails?.departureTime
        ? job?.flightConfirmationDetails?.departureTime?.split("T")?.[0]
        : job?.flightConfirmationDetails?.departureDate;

    taskObject = {
      active: false,
      completed: false,
      employeeId: job.employeeId,
      assignedTime: job.assignedTime,
      flightId: flightDocument?.flightId ?? null,
      flightInformation: {
        arrivalAirport: job?.flightConfirmationDetails?.destination,
        departureAirport: job?.flightConfirmationDetails?.origin,
        flightNumber: flightDocument?.flightNumber ?? "",
        flightTime: job?.flightConfirmationDetails?.destination == iata ? job?.flightConfirmationDetails?.arrivalTime : job?.flightConfirmationDetails?.departureTime,
        flightDate: flightDate,
        arrivalTime: job?.flightConfirmationDetails?.arrivalTime ?? null,
        departureTime: job?.flightConfirmationDetails?.departureTime ?? null,
        departureDate: job?.flightConfirmationDetails?.departureDate ?? null,
        // seatNumber: getSeatNumber(job),
        // Aircraft information
        aircraftType: flightDocument?.aircraftType || null,
        aircraftRegistration: flightDocument?.aircraftRegistration || null,
        aircraftModel: flightDocument?.aircraftModel || null,
        seats: flightDocument?.seats || null,
        seatCapacity: flightDocument?.seatCapacity || null,
        ambiliftType: flightDocument?.ambiliftType || null,
        portsOfCall: flightDocument?.portsOfCall || null,
      },
      passengerInformation: {
        assistance: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? [] : job?.assistance ?? [],
        title: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? "" : job?.name ?? "",
      },
      flightBooking: job?.flightBooking ? job?.flightBooking : false,
      allocatedTime: new Date(),
      _id: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1
        ? `${flightDocument?.flightNumber ?? ""}${flightDate}_${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}_IATA_${iata}`
        : `${job?._id}_IATA_${iata}`,
      cancelled: false,
      pending: false,
      selectedTimeline: selectedTimeline?._id ?? null,
      dropOffLocation: formatDropOffLocation(selectedDropOff),
      pickUpLocation: formatPickUpLocation(selectedPickUp),
      isTransitBooking: job?.isTransitBooking ? true : false,
      notPlanned: false,
      isEstimatedBooking: job?.isEstimatedBooking ? true : false,
      passengerDutTime: null,
      combinedBooking: false,
      passengers: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? selectedMultiplePAXTask?.map((passenger) => {
        return {
          assistance: passenger?.assistance ?? [],
          name: passenger?.originalBookingDoc?.name ?? passenger?.name ?? "n/a",
          originalBookingId: passenger?.originalBookingId ?? passenger?._id ?? null,
          bookingCreateDate: passenger?.originalBookingDoc?.dateCreated ?? passenger?.dateCreated ?? null,
        }
      }) : [],
      bookingCreateDate: job?.dateCreated ?? null,
      notes: job.notes ?? bookingNotes,
      requiredUpdates: job.notComplete ?? false,
      groupedBooking: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? true : false,
      assignedAsHC: assignedAsHC,
      ...(connectionFlightData || { connection: false, connectionFlight: null }),
    };
    if (job?.flightBooking) taskObject["bookingCreateDate"] = job?.originalBookingDoc?.dateCreated ?? null;
    // check if selectedTask has paxPositionInfo it's a checked in passenger booking add the checkInTime to task
    if (!Object.isObjectEmpty(confirmedTask?.paxPositionInfo)) { taskObject["checkInTime"] = confirmedTask?.paxPositionInfo?.time }
    const bookingNoteEntries = normalizeBookingNotes(bookingNotes);
    if (bookingNoteEntries.length > 0) {
      taskObject["taskNotes"] = bookingNoteEntries;
    }
    if (job.ambiliftRequired) taskObject["ambiliftRequired"] = true
    dispatch(setTimelineData({ data: null, job: taskObject }));

    // save task to firestore
    const res = await dispatch(createTask(taskObject));

    // check response from createTask is successful change the paxStatus in paxPositionInfo to assigned
    if (res?.meta?.requestStatus === "fulfilled") {
      // Clean up client-side duplicate from persistence if this was a locally-created duplicate
      if (job?.isClientDuplicate && job?._id) {
        dispatch(removeClientDuplicate(job._id));
      }
      if (clickedEmployeeId && !res.payload.queued) updateEmployeeStatusForAppVersion(clickedEmployeeId, "available");

      // if the job is departure and not combined and not duplicated 
      if (!job?.combinedBooking && !job?.combinedRampBooking && !job?._id?.includes("_part-") && !job?._id?.includes("-part")) {

        const payload = {
          taskId: taskObject?._id,
          body: {
            bookingsIds: taskObject?.passengers?.map((pax) => pax?.originalBookingId) ?? [job?._id],
            taskType: taskObject?.flightBooking ? "arrival" : "departure",
            toAssign: true,
            iata: dbUser.details.authority
          }
        };
        dispatch(selectPassengers(payload));
      }
    }
  }

  // handle clearing page, states and selected task list
  const resetPage = () => {

    // clear flags
    dispatch(clearCreateTaskStatus());
    dispatch(resetUpdateTaskFlags());

    // remove the created task from selected task list that needs to be assigned
    if (latestJob?.groupedBooking && latestJob?.passengers?.length > 1) {
      const finalSelectedTasksList = Array.isFullArray(selectedTasksList) ? selectedTasksList?.filter((item) => latestJob?.passengers?.map((pax) => !pax?.originalBookingId)?.includes(item?._id)) : [];
      dispatch(setSelectedTaskList(finalSelectedTasksList));
    } else {
      const finalSelectedTasksList = Array.isFullArray(selectedTasksList) ? selectedTasksList?.filter((item) => item?._id != latestJob?._id) : [];
      dispatch(setSelectedTaskList(finalSelectedTasksList));
    }

    // reset selected column
    props?.setClickedColumn(false);

    // reset HC
    setAssignedAsHC(false);
  };

  const reset = () => {
    // need to reset data back to original state   
    props.taskRejected();
    dispatch(setSelectedTaskList([]));
  };

  // format drop off location to result object with location id and location name 
  const formatDropOffLocation = (id) => {
    if (!id) return null;
    const location = dropOffLocations.find((location) => location._id === id);
    if (location) return { id: location._id, name: location.title };
    else return null
  }

  // format pick up location to result object with location id and location name 
  const formatPickUpLocation = (id) => {
    if (!id) return null;
    const location = pickUpLocations.find((location) => location._id === id);
    if (location) return { id: location._id, name: location.title };
    else return null
  }

  const handleSwitchSelectMultiplePax = () => {
    setForMultiplePaxAssign(!forMultiplePaxAssign);
    setClicked([]);
    dispatch(setSelectedTask(null));
    dispatch(setSelectedMultiplePAXTask([]));
    dispatch(setSelectedMultiplePAXTaskError(null));
  }

  function getPaxName() {
    if (!doc) return ""
    if (doc.name) return processName(doc.name);
    if (doc.combinedBooking) return "Boarding task";
    if (doc.combinedRampBooking) return "Ramp task";
    if (doc.passengerInformation?.title && doc.passengerInformation?.title != "N/A") return processName(doc.passengerInformation?.title)
    return "N/A"
  }

  function getPassengerName(task) {
    let name = "N/A"
    if (!task) return name
    if (task.name) name = processName(task.name);
    if (task.combinedBooking) name = `Boarding ${selectedTask?.passengers.length ?? 0} pax`;
    if (task.combinedRampBooking) name = `Ramp ${selectedTask?.passengers.length ?? 0} pax`;
    if (forMultiplePaxAssign) name = multiplePax;
    return name
  }

  // check between selectedTask and selectedMultiPAXTask to determine if any pax has WCHC
  function hasWCHCAssistance() {
    const codes = ['WCHC'];

    const hasCodes = (assistance) =>
      assistance?.some(str =>
        codes.some(code => String(str)?.toUpperCase()?.includes(code))
      );

    if (forMultiplePaxAssign) {
      return Array.isArray(selectedMultiplePAXTask)
        ? selectedMultiplePAXTask?.some(pax => hasCodes(pax?.assistance))
        : false;
    }

    return selectedTask ? hasCodes(selectedTask?.assistance) : false;
  }

  //#endregion

  return (
    <div>

      <TabSwitcherSmall
        tabs={confirmationTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabsListStyle={{ marginBottom: '16px' }}
      >

        {/* Create Adhoc Tab */}
        {activeTab === 'create-adhoc' && (
          <CreateAdhocTask onClose={props.taskConfirmed} />
        )}

        {/* Assign Passenger Tab */}
        {activeTab === 'assign-passenger' && (
          <div>

            {/* create task modal body */}
            <div style={{ opacity: createTaskStatus == "loading" || updateTaskStatus == "loading" || getFlightByIdStatus == "loading" ? 0.2 : 1.0, flex: 1 }}>

              {/* title + task list + employee info text */}
              <div style={{ flex: 5 }}>

                {selectedTasksList?.length > 1 &&
                  //  iata !== "JER" &&
                  <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_10, marginBottom: Spacing.SCALE_10, marginTop: Spacing.SCALE_10 }}>
                    <p className="mb-0">Turn on to select multiple PAX and assign them</p>
                    <Form>
                      <Form.Check
                        onChange={handleSwitchSelectMultiplePax}
                        type="switch"
                        id="custom-switch"
                        checked={forMultiplePaxAssign}
                      />
                    </Form>
                  </div>
                }

                {selectedMultiplePAXTaskError && <p className="mb-0" style={{ color: 'red' }}>{selectedMultiplePAXTaskError}</p>}

                {/* pax assignment info text */}
                <PaxAssignmentText selectedName={selectedName} flightNumber={flightDocument?.flightNumber ?? ""} originAirport={origin} destinationAirport={destination} flightTime={flightDocument?.operationTime} selectedEmployee={selectedEmployee} assignedTime={props?.clickedColumn ? props?.clickedColumn?.split("-")[0] : ""} />

                {/* selected task list */}
                <div style={{ ...JobConfirmationStyles.taskListContainer, justifyContent: "flex-start", alignItems: 'flex-start' }} >
                  <div className='mt-2' style={{ display: 'flex', justifyContent: "flex-start", alignItems: 'flex-start', marginBottom: Spacing.SCALE_12 }}>
                    {selectedTasksList?.map((task, index) => (
                      <div key={index} style={{ marginLeft: index == 0 ? 0 : Spacing.SCALE_12, borderWidth: 2.5, borderStyle: 'solid', borderRadius: 10, borderColor: task?.['_id'] == selectedTask?.['_id'] || (forMultiplePaxAssign && selectedMultiplePAXTask?.map((item) => item?._id)?.includes(task?._id)) ? Colors.PRM_LIGHT_BLUE : 'white' }}>
                        <JobCard
                          job={task}
                          index={index}
                          clicked={clicked || (forMultiplePaxAssign && selectedMultiplePAXTask?.includes(task?._id))}
                          setClicked={setClicked}
                          toConfirm={true}
                          jobConfirmation={true}
                          forMultiplePaxAssign={forMultiplePaxAssign}
                          flightData={task?._id === selectedTask?._id ? flightDocument : null}
                          flightsArray={flightsArray}
                          thirtyMinLayout={thirtyMinLayout}
                          selectedTask={selectedTask}
                          selectedTasksList={selectedTasksList}
                          usedTheme={usedTheme}
                          selectedMultiplePAXTask={selectedMultiplePAXTask}
                          showFullCard={true}
                        />
                      </div>
                    ))}
                  </div>

                  {/* assign task as HC - arrival only, top right of task list */}
                  {flightDocument?.type === 'arrival' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_10, marginLeft: 'auto', alignSelf: 'flex-start' }}>
                      <Form.Check
                        type="switch"
                        id="assigned-as-hc"
                        checked={assignedAsHC}
                        onChange={(e) => setAssignedAsHC(e.target.checked)}
                      />
                      <label htmlFor="assigned-as-hc">Assign as HC (WCHC Chair Lift)</label>
                    </div>
                  )}
                  {/* if HC but assigned pax/pax list has no WCHC, show warning */}
                  {assignedAsHC && !hasWCHCAssistance() && (
                    <div style={{ ...JobConfirmationStyles.warningMessageContainer, backgroundColor: Colors.LIGHT_RED, borderLeftColor: Colors.DARK_RED, marginTop: Spacing.SCALE_8 }}>
                      <div style={JobConfirmationStyles.warningMessageDiv}>
                        <IoWarning size={Spacing.SCALE_28} color={Colors.DARK_RED} />
                        <p style={{ ...JobConfirmationStyles.warningMessageText, color: Colors.DARK_RED }}>
                          You have assigned this as a WCHC Chair Lift task, but the passenger(s) do not have WCHC assistance.
                        </p>
                      </div>
                    </div>
                  )}
                </div>


                {/* connection passenger info banner */}
                {(selectedTask?.originalBookingDoc?.onwards || selectedTask?.inbound || selectedTask?.connection) && (
                  <ConnectionBanner
                    connectionFlight={connectionFlightData?.connectionFlight || selectedTask?.originalBookingDoc?.onwards || selectedTask?.inbound}
                    isOnwards={!!selectedTask?.originalBookingDoc?.onwards}
                  />
                )}


                {/* task + employee info + warning message + error message */}
                <div className='mt-3'>

                  {/* pax name, employee name, employee id, assigned time */}
                  <div style={{ display: "flex", flex: 1, justifyContent: "space-between" }}>

                    {/* selected pax name */}
                    <div style={{ flex: 1 }}>
                      <div>
                        <Subtitle>{'Passenger'}</Subtitle>
                        <p>{selectedName}</p>
                      </div>
                    </div>

                    {/* assigned employee name */}
                    <div style={{ flex: 1 }}>
                      <Subtitle>Employee Name</Subtitle>
                      <p>{selectedEmployee ? selectedEmployee?.title : ""}</p>
                    </div>

                    {/* flight time */}
                    <div style={{ flex: 1 }}>
                      <Subtitle>{flightDocument?.type == 'departure' || flightDocument?.type == 'boarding' ? 'Departure Time' : 'Arrival Time'}</Subtitle>
                      <p>{flightTime}</p>
                    </div>

                    {/* assigned time */}
                    <div style={{ flex: 1, }}>
                      <Subtitle>Assigned Time</Subtitle>
                      <p>{props?.clickedColumn ? props?.clickedColumn?.split("-")[0] : ""}</p>
                    </div>

                    {/* ALT pickup time - only show for ALT pickup tasks */}
                    {selectedTask?.isALTPickUp && selectedTask?.pickUpTime && (
                      <div style={{ flex: 1, }}>
                        <Subtitle style={{ color: 'purple' }}>Pickup Time</Subtitle>
                        <p>
                          {selectedTask.pickUpTime?.seconds
                            ? new Date(selectedTask.pickUpTime.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : new Date(selectedTask.pickUpTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          }
                        </p>
                      </div>
                    )}

                  </div>

                  {/* pick up Location, drop off location and booking notes */}
                  <div className="mt-3">
                    <PickupDropOffDropDowns
                      selectedPickUp={selectedPickUp}
                      selectedDropOff={selectedDropOff}
                      setSelectedPickUp={(val) => { setSelectedPickUp(val) }}
                      setSelectedDropOff={(val) => { setSelectedDropOff(val) }}
                      flightDocument={flightDocument}
                      selectedEmployee={selectedEmployee}
                      connectionFlightData={connectionFlightData}
                    />
                  </div>

                  {/* booking notes */}
                  <Row className='mt-4'>
                    <BookingNotesSuggestions setBookingNotes={setBookingNotes} bookingNotes={bookingNotes} />
                  </Row>

                  {/* create task error message */}
                  <p>{error ? error : ""}</p>
                  {localError ? <p style={{ color: 'red' }}>{localError}</p> : null}
                  {getFlightByIdStatus === "failed" && (
                    <p style={{ color: 'red' }}>Failed to load flight data. Please close and try again.</p>
                  )}
                </div>
              </div>

              {/* confirm / reset buttons */}
              <div className='mt-5' style={{ display: "flex", justifyContent: "space-between", flex: 1 }}>
                <Button variant="danger" onClick={reset} >{"Reset Assignment"}</Button>
                <Button variant="primary"
                  onClick={() => forMultiplePaxAssign ? handleMultiplePaxAssign() : confirm()}
                  disabled={
                    connectionFlightLoading ||
                    getFlightByIdStatus === "failed" ||
                    (!selectedTask && !forMultiplePaxAssign) ||
                    (forMultiplePaxAssign && !Array.isFullArray(selectedMultiplePAXTask))}>
                  {connectionFlightLoading
                    ? <><CustomSpinner size='small' /> Loading flight info...</>
                    : selectedEmployee?.title ? "Assign Task to " + selectedEmployee?.title : "Confirm Task Assignment"}
                </Button>
              </div>

            </div>

            {/* loading spinner */}
            {createTaskStatus == "loading" || updateTaskStatus == "loading" && (
              <div
                style={{
                  position: 'absolute',   // makes the spinner overlay the content
                  top: '30%',             // vertically center
                  left: '50%',            // horizontally center
                  transform: 'translate(-50%, -50%)',  // exact centering
                  zIndex: 9999            // makes sure it's on top of other content
                }}>
                <CustomSpinner rowCenter={true} />
              </div>
            )}
          </div>
        )}

      </TabSwitcherSmall>
    </div>

  );
};

export default JobConfirmation;